import {
  tableV2NestedCarrierRowSx,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
  tableV2NestedDetailCellSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2PassRateCell,
  formatTableV2DateTime,
  formatTableV2PassRate,
  tableV2RelativeTimeSx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { r, s, typographyTokens } from '@/theme/tokens';
import { Chip, getStatusChipColor, resolveStatusChipTone } from '@/ui/primitives';
import type {
  ExecutionRunDto,
  ExecutionStatusKey,
  SuiteRunStatus,
  TestRunStatus,
} from '@flowqa/shared';
import type { ReactElement } from 'react';

const statusColorVars = {
  neutral: 'var(--color-neutral)',
  green: 'var(--color-green)',
  red: 'var(--color-red)',
  orange: 'var(--color-orange)',
  brown: 'var(--color-brown)',
  teal: 'var(--color-teal)',
  purple: 'var(--color-purple)',
  pink: 'var(--color-pink)',
  blue: 'var(--color-blue)',
} as const;

const resolveStatusColorVar = (
  status: SuiteRunStatus | TestRunStatus | ExecutionStatusKey | null | undefined,
): string => statusColorVars[getStatusChipColor(status)];

export type ExecutionRunCounts = {
  total: number;
  passed: number;
  failed: number;
  errored: number;
  skipped: number;
  untested: number;
};

export const countSuites = (row: ExecutionRunDto): ExecutionRunCounts => ({
  total:
    (row.suiteRunPassed ?? 0) +
    (row.suiteRunFailed ?? 0) +
    (row.suiteRunErrored ?? 0) +
    (row.suiteRunSkipped ?? 0) +
    (row.suiteRunUntested ?? 0),
  passed: row.suiteRunPassed ?? 0,
  failed: row.suiteRunFailed ?? 0,
  errored: row.suiteRunErrored ?? 0,
  skipped: row.suiteRunSkipped ?? 0,
  untested: row.suiteRunUntested ?? 0,
});

export const countTests = (row: ExecutionRunDto): ExecutionRunCounts => ({
  total:
    (row.testRunPassed ?? 0) +
    (row.testRunFailed ?? 0) +
    (row.testRunErrored ?? 0) +
    (row.testRunSkipped ?? 0) +
    (row.testRunUntested ?? 0),
  passed: row.testRunPassed ?? 0,
  failed: row.testRunFailed ?? 0,
  errored: row.testRunErrored ?? 0,
  skipped: row.testRunSkipped ?? 0,
  untested: row.testRunUntested ?? 0,
});

export const formatPassRate = (counts: ExecutionRunCounts): string => formatTableV2PassRate(counts);

export const formatUploadedAt = (value: Date | string | null | undefined): string =>
  formatTableV2DateTime(value);

export const relativeTimeSx = tableV2RelativeTimeSx;

export const nestedCarrierRowSx = tableV2NestedCarrierRowSx;
export const nestedDetailCellSx = tableV2NestedDetailCellSx;
export const nestedDetailContentSx = tableV2NestedContentSx;
export const nestedTestTableFrameSx = tableV2NestedDepthSx[2].frame;
export const nestedTestTableSurfaceSx = tableV2NestedDepthSx[2].table;

export const StatusChip = ({
  label,
  status,
}: {
  label: string;
  status: SuiteRunStatus | TestRunStatus | null | undefined;
}): ReactElement => {
  return (
    <Chip
      size="small"
      tone={resolveStatusChipTone(status)}
      label={label}
      sx={{
        border: `1px solid ${resolveStatusColorVar(status)}`,
        borderRadius: r('sm'),
        backgroundColor: 'transparent',
        color: resolveStatusColorVar(status),
        fontWeight: typographyTokens.caption.fontWeight,
        height: '30px',
        '& .MuiChip-label': {
          color: 'inherit',
          px: s('lg'),
          py: s('xs'),
        },
        '& .MuiChip-label .MuiTypography-root': {
          color: resolveStatusColorVar(status),
          fontWeight: typographyTokens.caption.fontWeight,
        },
        '&:hover, &.Mui-focusVisible, &:active': {
          backgroundColor: 'transparent',
        },
      }}
    />
  );
};

export const PassRateCell = ({ counts }: { counts: ExecutionRunCounts }): ReactElement => (
  <TableV2PassRateCell counts={counts} />
);
